import { FC } from "react";
import { Text, Image } from "@mantine/core";
import downloadbutton from "../../../../assets/download_24px.svg";
import { File as FileEnum } from "@funded-here-interface/common/src/constant/enum";
import { useStyles } from "./DocList.styles";

interface FailedDocument {
  fileName: string;
  [key: string]: any;
}

interface DocumentListProps {
  documents: string[];
  header: string;
  fileType: FileEnum;
  downloadDocument: (path: string, fileName: string) => void;
  getNameFromPath: (path: string) => string;
  toggleExcludeDocument: (path: string) => void;
  excludedFiles: string[]; // Add this prop
  hasBeenSentToWallex: boolean;
  uploadedFiles: string[];
  failedDocuments: (string | FailedDocument)[];
  uploadPartialSuccess: boolean;
}

const DocList: FC<DocumentListProps> = ({
  documents,
  header,
  fileType,
  downloadDocument,
  getNameFromPath,
  toggleExcludeDocument,
  excludedFiles,
  hasBeenSentToWallex,
  uploadedFiles = [],
  failedDocuments = [],
  uploadPartialSuccess = false,
}) => {
  const { classes } = useStyles();

  // Get all documents for this category, including excluded ones
  // This ensures excluded documents are still visible
  const allDocumentsForCategory = [...documents];

  return (
    <>
      <Text className={classes.header}>{header}</Text>

      {allDocumentsForCategory.map((doc) => {
        const isExcluded = excludedFiles.includes(doc);
        const fileName = getNameFromPath(doc);

        // Check if document is uploaded
        const isUploaded =
          Array.isArray(uploadedFiles) &&
          uploadedFiles.length > 0 &&
          uploadedFiles.some((uploadedFile) => uploadedFile === fileName);

        // Check if document failed to upload
        const hasFailed =
          Array.isArray(failedDocuments) &&
          failedDocuments.length > 0 &&
          failedDocuments.some((failedDoc) => {
            if (typeof failedDoc === "string") {
              return failedDoc === fileName;
            } else if (failedDoc && failedDoc.fileName) {
              return failedDoc.fileName === fileName;
            }
            return false;
          });

        return (
          <div key={doc} className={classes.DocContain}>
            <Text
              className={classes.subDoc}
              style={isExcluded ? { color: "#888" } : {}}
            >
              {getNameFromPath(doc)}
              {isExcluded && (
                <span style={{ color: "red", marginLeft: "10px" }}>
                  <b>Excluded</b>
                </span>
              )}
            </Text>

            {/* Status indicator - show for both uploaded and failed docs */}
            {(isUploaded || (uploadPartialSuccess && hasFailed)) &&
              hasBeenSentToWallex && (
                <div
                  style={{
                    marginLeft: "10px",
                    padding: "3px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: 500,
                    backgroundColor: isUploaded
                      ? "#e6f7ee"
                      : hasFailed
                        ? "#ffeeee"
                        : "transparent",
                    border: isUploaded
                      ? "1px solid #a8e6c1"
                      : hasFailed
                        ? "1px solid #ffcccc"
                        : "none",
                    maxHeight: "20px",
                    maxWidth: "150px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isUploaded && (
                    <span
                      style={{
                        color: "#2e7d32",
                        maxHeight: "20px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      Sent to Wallex
                    </span>
                  )}
                  {hasFailed && !isUploaded && (
                    <span
                      style={{
                        color: "#d32f2f",
                        maxHeight: "20px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      Failed
                    </span>
                  )}
                </div>
              )}

            <Image
              onClick={() => downloadDocument(doc, getNameFromPath(doc))}
              src={downloadbutton}
              className={classes.downloadIcon}
            />

            {!hasBeenSentToWallex &&
              !isUploaded &&
              !(
                uploadPartialSuccess &&
                uploadedFiles.some((file) => {
                  // Get filename from doc path
                  const docFileName = getNameFromPath(doc);
                  // Check if this file has been successfully uploaded
                  return docFileName.includes(file);
                })
              ) && (
                <div className="button-container">
                  <span
                    onClick={() => toggleExcludeDocument(doc)}
                    className={classes.deleteButton}
                    style={isExcluded ? { color: "green" } : {}}
                  >
                    {isExcluded ? "Restore" : "Exclude"}
                  </span>
                </div>
              )}
          </div>
        );
      })}
    </>
  );
};

export default DocList;
